import React, { useState, useRef, useEffect, useCallback } from 'react';
import { faqAPI } from '../../lib/api';
import { Button } from './button';
import { Input } from './input';
import { Card } from './card';
import { ScrollArea } from './scroll-area';
import { 
  MessageCircle, 
  X, 
  Send, 
  ChefHat,
  ArrowLeft,
  Sparkles,
  Users,
  Store,
  HelpCircle,
  ChevronRight
} from 'lucide-react';

// Smart search keywords and responses
const SMART_KEYWORDS = {
  'fssai': {
    response: "🔐 **FSSAI Registration**\n\nFSSAI is required for all HomeCooks. A basic FSSAI registration is needed before you can start selling food on RuchiBox.\n\n**Would you like help registering?**\n\n1️⃣ Yes, guide me\n2️⃣ No, I'm already registered\n3️⃣ Back to main menu",
  },
  'gst': {
    response: "📋 **GST Registration**\n\nGST registration is only required if your annual turnover exceeds ₹20 lakhs (or ₹10 lakhs in special category states).\n\nFor most HomeCooks starting out, GST is not immediately required."
  },
  'payment': {
    response: "💰 **Payments on RuchiBox**\n\nWe support multiple payment methods:\n• UPI (Google Pay, PhonePe, Paytm)\n• Credit/Debit Cards\n• Net Banking\n• RuchiBox Wallet\n\n**For Sellers:** Weekly settlements to your bank account."
  },
  'refund': {
    response: "🔄 **Refund Policy**\n\nRefunds are processed as:\n• Instant wallet credit, OR\n• Bank reversal within 5-7 business days\n\nYou can request a refund if:\n• Order was delayed significantly\n• Food quality issues\n• Wrong order delivered"
  },
  'delivery': {
    response: "🚚 **Delivery Information**\n\nDelivery is handled by:\n• Society delivery staff, OR\n• Platform logistics (optional)\n\n**Features:**\n✅ Contactless delivery available\n✅ Schedule orders for next day\n✅ Track your order in real-time"
  },
  'subscription': {
    response: "📅 **Subscription Meals**\n\nYes! We support:\n• Weekly meal plans\n• Monthly tiffin services\n• Daily recurring orders (milk, bread, etc.)\n\nSubscriptions offer:\n✅ Discounted rates\n✅ No daily ordering hassle\n✅ Flexible pause/cancel"
  },
  'hygiene': {
    response: "🧼 **Safety & Hygiene Standards**\n\nAll RuchiBox HomeCooks:\n✅ Are verified for hygiene standards\n✅ Follow food safety guidelines\n✅ Use food-grade packaging\n✅ Undergo periodic audits"
  },
  'support': {
    response: "🆘 **Customer Support**\n\nWe're here to help!\n\n📧 Email: support@ruchibox.in\n📞 Phone: Available in-app\n💬 Chat: You're already here!\n\nTypical response time: Within 24 hours"
  }
};

const CATEGORY_INFO = {
  homecook: { label: 'HomeCooks', icon: ChefHat, emoji: '🍳' },
  resident: { label: 'Residents', icon: Users, emoji: '🍽️' },
  vendor: { label: 'Vendors', icon: Store, emoji: '🏪' }
};

const formatMessage = (text) => {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br />');
};

export function CravyChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [currentView, setCurrentView] = useState('main'); // main, category, faq-list
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [faqs, setFaqs] = useState({ homecook: [], resident: [], vendor: [] });
  const [isTyping, setIsTyping] = useState(false);
  const [faqsLoaded, setFaqsLoaded] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Load FAQs from backend
  useEffect(() => {
    const loadFAQs = async () => {
      try {
        const response = await faqAPI.getAll(null, true);
        const grouped = { homecook: [], resident: [], vendor: [] };
        response.data.forEach(faq => {
          if (grouped[faq.category]) {
            grouped[faq.category].push(faq);
          }
        });
        setFaqs(grouped);
        setFaqsLoaded(true);
      } catch (error) {
        console.error('Failed to load FAQs:', error);
        // FAQs will be empty, chatbot will still work with smart keywords
      }
    };
    loadFAQs();
  }, []);

  // Initialize welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addBotMessage(getWelcomeMessage(), 0);
    }
  }, [isOpen, messages.length]);

  const getWelcomeMessage = () => {
    return `Hey there! 👋 I'm **Cravy**, your friendly RuchiBox assistant!

Your neighbourhood food marketplace for HomeCooks, Residents & Local Vendors.

**How can I help you today?**

1️⃣ I am a HomeCook
2️⃣ I want to Order Food (Resident)
3️⃣ I am a Society Vendor
4️⃣ Payments & Refunds
5️⃣ Safety & Hygiene
6️⃣ Talk to Support

*Reply with a number or just type your question!*`;
  };

  const addBotMessage = (content, delay = 500) => {
    if (delay > 0) {
      setIsTyping(true);
    }
    setTimeout(() => {
      setMessages(prev => [...prev, { type: 'bot', content }]);
      setIsTyping(false);
    }, delay);
  };

  const checkSmartKeywords = (text) => {
    const lowerText = text.toLowerCase();
    for (const [keyword, data] of Object.entries(SMART_KEYWORDS)) {
      if (lowerText.includes(keyword)) {
        return data.response;
      }
    }
    return null;
  };

  const searchFAQs = (query) => {
    const lowerQuery = query.toLowerCase();
    const allFaqs = [...faqs.homecook, ...faqs.resident, ...faqs.vendor];
    
    for (const faq of allFaqs) {
      const questionLower = faq.question.toLowerCase();
      if (questionLower.includes(lowerQuery) || 
          lowerQuery.split(' ').some(word => word.length > 3 && questionLower.includes(word))) {
        return `**${faq.question}**\n\n${faq.answer}\n\n*Type 'menu' to see all options.*`;
      }
    }
    return null;
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setCurrentView('faq-list');
    const info = CATEGORY_INFO[category];
    const categoryFaqs = faqs[category] || [];
    
    if (categoryFaqs.length === 0) {
      addBotMessage(`${info.emoji} **${info.label} FAQs**\n\nNo FAQs available yet. Please type your question and I'll try to help!`);
    } else {
      addBotMessage(`${info.emoji} **${info.label} FAQs**\n\nSelect a question below or type your own:`);
    }
  };

  const handleFAQSelect = (faq) => {
    setMessages(prev => [...prev, { type: 'user', content: faq.question }]);
    addBotMessage(`**${faq.question}**\n\n${faq.answer}\n\n*Type 'menu' to go back or ask another question!*`);
  };

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
    setInput('');
    setCurrentView('main'); // Reset view after sending message

    processMessage(userMessage);
  };

  const processMessage = (text) => {
    const lowerText = text.toLowerCase();
    
    // Check for menu/back commands
    if (lowerText === 'menu' || lowerText === 'main menu' || lowerText === 'back' || lowerText === '0') {
      setCurrentView('main');
      setSelectedCategory(null);
      addBotMessage(getWelcomeMessage());
      return;
    }

    // Check smart keywords first
    const smartResponse = checkSmartKeywords(text);
    if (smartResponse) {
      addBotMessage(smartResponse);
      return;
    }

    // Handle number inputs
    if (/^[1-6]$/.test(text)) {
      const num = parseInt(text);
      
      switch (num) {
        case 1:
          handleCategorySelect('homecook');
          return;
        case 2:
          handleCategorySelect('resident');
          return;
        case 3:
          handleCategorySelect('vendor');
          return;
        case 4:
          addBotMessage(SMART_KEYWORDS.payment.response);
          return;
        case 5:
          addBotMessage(SMART_KEYWORDS.hygiene.response);
          return;
        case 6:
          addBotMessage(SMART_KEYWORDS.support.response);
          return;
        default:
          break;
      }
    }

    // Search FAQs
    const faqResponse = searchFAQs(text);
    if (faqResponse) {
      addBotMessage(faqResponse);
      return;
    }

    // Default response
    addBotMessage(`I'm not sure I understood that. 🤔\n\nHere are some things I can help with:\n• Type **'FSSAI'** for license info\n• Type **'payment'** for payment queries\n• Type **'menu'** to see all options\n• Or ask me any question!\n\n*Tip: Try typing keywords like 'refund', 'delivery', 'hygiene', etc.*`);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const goToMainMenu = () => {
    setCurrentView('main');
    setSelectedCategory(null);
    addBotMessage(getWelcomeMessage());
  };

  // Render FAQ list for selected category
  const renderFAQList = () => {
    if (!selectedCategory || currentView !== 'faq-list') return null;
    
    const categoryFaqs = faqs[selectedCategory] || [];
    if (categoryFaqs.length === 0) return null;

    return (
      <div className="px-3 pb-2 shrink-0">
        <p className="text-xs text-gray-500 mb-1.5 px-1">Select a question:</p>
        <div className="bg-gray-50 rounded-xl p-1.5 space-y-0.5 max-h-28 overflow-y-auto">
          {categoryFaqs.map((faq, idx) => (
            <button
              key={faq.id}
              onClick={() => handleFAQSelect(faq)}
              className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-white hover:shadow-sm transition-all text-xs flex items-center gap-2 group"
              data-testid={`faq-item-${idx}`}
            >
              <span className="text-orange-500 font-bold shrink-0 w-4">{idx + 1}.</span>
              <span className="text-gray-700 group-hover:text-gray-900 line-clamp-1 flex-1">{faq.question}</span>
              <ChevronRight className="w-3 h-3 text-gray-400 group-hover:text-orange-500 shrink-0" />
            </button>
          ))}
        </div>
        <button
          onClick={goToMainMenu}
          className="w-full mt-1.5 text-xs text-orange-500 hover:text-orange-600 flex items-center justify-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" />
          Back to menu
        </button>
      </div>
    );
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          isOpen ? 'bg-gray-600 rotate-90' : 'bg-gradient-to-br from-orange-500 to-red-500'
        }`}
        data-testid="cravy-chat-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-24 right-6 z-50 w-[400px] h-[600px] flex flex-col shadow-2xl rounded-2xl overflow-hidden border-0" data-testid="cravy-chat-window">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 p-3 flex items-center gap-3 shrink-0">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-white flex items-center gap-1 text-sm">
                Cravy
                <Sparkles className="w-3.5 h-3.5" />
              </h3>
              <p className="text-xs text-white/80">RuchiBox Assistant</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 h-8 w-8"
              onClick={() => {
                setMessages([]);
                setCurrentView('main');
                setSelectedCategory(null);
                setTimeout(() => addBotMessage(getWelcomeMessage(), 0), 100);
              }}
              title="Reset chat"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="space-y-4">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
                      msg.type === 'user'
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-br-md'
                        : 'bg-gray-100 text-gray-800 rounded-bl-md'
                    }`}
                  >
                    <div 
                      className="text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                    />
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                  </div>
                </div>
              )}
              {/* Scroll anchor */}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* FAQ List (when category selected) */}
          {renderFAQList()}

          {/* Input */}
          <div className="p-3 border-t bg-white shrink-0">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message or number..."
                className="flex-1 rounded-full border-gray-200 focus:border-orange-400"
                data-testid="cravy-chat-input"
              />
              <Button
                onClick={handleSend}
                className="rounded-full w-10 h-10 p-0 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
                data-testid="cravy-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-gray-400 text-center mt-2">
              Powered by RuchiBox • Type &apos;menu&apos; anytime
            </p>
          </div>
        </Card>
      )}
    </>
  );
}

export default CravyChatbot;
